import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import usePlans from '@alltrails/context/hooks/usePlans';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type { SubscriptionFeatures } from '@alltrails/types/SubscriptionFeatures';
import ContentBlock from '../ContentBlock/ContentBlock';
import peakFeatures from '../GenericPeakUpsell/peakFeatures';
import PlusInfoModule from '../GenericPeakUpsell/PlusInfoModule';
import PeakInfoModule from '../GenericPlusUpsell/PeakInfoModule';
import plusFeatures from '../GenericPlusUpsell/plusFeatures';
import styles from '../GenericUpsell.module.scss';
import SocialProof from '../SocialProof/SocialProof';
import TrialReminder from '../TrialReminder/TrialReminder';
import UpsellFeatureList from '../UpsellFeatureList/UpsellFeatureList';

type Props = {
  featureListLabel?: ReactNode;
  heading: ReactNode;
  isPromoEligible: boolean;
  promoEligibleHeading: ReactNode;
  promoEligibleSubheading?: ReactNode;
  subheading?: ReactNode;
  subscriptionFeatures?: SubscriptionFeatures;
  tier: PurchaseTier;
  upsellTrigger: UpsellTrigger;
};

// Shared body for the non-tiered Plus/Peak upsells. Eligibility is resolved by the parent and passed in.
export default function GenericUpsellContent({
  featureListLabel,
  heading,
  isPromoEligible,
  promoEligibleHeading,
  promoEligibleSubheading,
  subheading,
  subscriptionFeatures,
  tier,
  upsellTrigger
}: Props) {
  const plans = usePlans();
  const currencyCode = useCurrencyCode();
  const intl = useIntl();
  const plan = tier === 'peak' ? plans.peak : plans.plus;
  const features = subscriptionFeatures ?? (tier === 'peak' ? peakFeatures(intl) : plusFeatures(intl));

  return (
    <div className={styles.body}>
      <ContentBlock
        heading={heading}
        isPromoEligible={isPromoEligible}
        promoEligibleHeading={promoEligibleHeading}
        promoEligibleSubheading={promoEligibleSubheading}
        subheading={subheading}
        tier={tier}
      />
      {isPromoEligible && <TrialReminder cost={plan.priceTotal} currencyCode={currencyCode} tier={tier} />}
      <UpsellFeatureList features={features} {...(featureListLabel ? { label: featureListLabel } : {})} />
      {tier === 'peak' && <PlusInfoModule />}
      {tier === 'plus' && <PeakInfoModule upsellTrigger={upsellTrigger} />}
      <SocialProof />
    </div>
  );
}
